// src/utils/photoCache.js
// LRU (Least Recently Used) cache for photo Object URLs
// Prevents repeated IndexedDB reads and manages Object URL lifecycle

/**
 * LRU Cache implementation with memory tracking
 * Automatically evicts oldest entries when limits are reached
 */
class LRUCache {
  /**
   * @param {number} maxEntries - Maximum number of entries
   * @param {number} maxMemoryMB - Maximum memory in megabytes
   */
  constructor(maxEntries = 100, maxMemoryMB = 200) {
    this.cache = new Map();
    this.maxEntries = maxEntries;
    this.maxMemoryBytes = maxMemoryMB * 1024 * 1024;
    this.currentMemoryBytes = 0;
    this.hits = 0;
    this.misses = 0;
  }
  
  /**
   * Get a value from the cache
   * @param {string} key 
   * @returns {*} The cached value or null
   */
  get(key) {
    if (!this.cache.has(key)) {
      this.misses++;
      return null;
    }
    
    this.hits++;
    
    // Move to end (most recently used)
    const entry = this.cache.get(key);
    this.cache.delete(key);
    this.cache.set(key, entry);
    
    return entry.value;
  }
  
  /**
   * Set a value in the cache
   * @param {string} key 
   * @param {*} value 
   * @param {number} sizeBytes - Estimated size in bytes
   */
  set(key, value, sizeBytes = 0) {
    // Remove if exists (to update position)
    if (this.cache.has(key)) {
      const old = this.cache.get(key);
      this.currentMemoryBytes -= old.size;
      this.revokeIfObjectUrl(old.value);
      this.cache.delete(key);
    }
    
    // Evict oldest entries if needed
    while (
      (this.cache.size >= this.maxEntries || 
       this.currentMemoryBytes + sizeBytes > this.maxMemoryBytes) &&
      this.cache.size > 0
    ) {
      this.evictOldest();
    }
    
    // Add new entry
    this.cache.set(key, { 
      value, 
      size: sizeBytes,
      addedAt: Date.now()
    });
    this.currentMemoryBytes += sizeBytes;
  }
  
  /**
   * Check if key exists in cache
   * @param {string} key 
   * @returns {boolean}
   */
  has(key) {
    return this.cache.has(key);
  }
  
  /**
   * Delete a specific key
   * @param {string} key 
   * @returns {boolean} True if key existed
   */
  delete(key) {
    if (this.cache.has(key)) {
      const entry = this.cache.get(key);
      this.currentMemoryBytes -= entry.size;
      this.revokeIfObjectUrl(entry.value);
      this.cache.delete(key);
      return true;
    }
    return false;
  }
  
  /**
   * Clear all entries
   */
  clear() {
    for (const [, entry] of this.cache) {
      this.revokeIfObjectUrl(entry.value);
    }
    this.cache.clear();
    this.currentMemoryBytes = 0;
    this.hits = 0;
    this.misses = 0;
  }
  
  /**
   * Evict the oldest (least recently used) entry
   */
  evictOldest() {
    const oldestKey = this.cache.keys().next().value;
    if (oldestKey !== undefined) {
      const entry = this.cache.get(oldestKey);
      this.currentMemoryBytes -= entry.size;
      this.revokeIfObjectUrl(entry.value);
      this.cache.delete(oldestKey);
    }
  }
  
  /**
   * Revoke Object URL if the value is one
   * This prevents memory leaks
   */
  revokeIfObjectUrl(value) {
    if (typeof value === 'string' && value.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(value);
      } catch (err) {
        // Ignore errors - URL may already be revoked
      }
    }
  }
  
  /**
   * Get cache statistics
   */
  get stats() {
    const hitRate = this.hits + this.misses > 0
      ? ((this.hits / (this.hits + this.misses)) * 100).toFixed(1)
      : '0';
    
    return {
      entries: this.cache.size,
      maxEntries: this.maxEntries,
      memoryMB: (this.currentMemoryBytes / (1024 * 1024)).toFixed(1),
      maxMemoryMB: (this.maxMemoryBytes / (1024 * 1024)).toFixed(0),
      hits: this.hits,
      misses: this.misses,
      hitRate: hitRate + '%'
    };
  }
  
  /**
   * Get all keys in order (oldest first)
   */
  keys() {
    return Array.from(this.cache.keys());
  }
  
  /**
   * Preload entries (doesn't update LRU order)
   * Useful for bulk loading
   */
  preload(entries) {
    for (const [key, value, size] of entries) {
      if (!this.has(key)) {
        this.set(key, value, size);
      }
    }
  }
}

// ============ Singleton Cache Instances ============

/**
 * Cache for full photo Object URLs
 * - Larger entries (photos can be 2-10MB)
 * - Fewer entries, more memory per entry
 */
export const photoUrlCache = new LRUCache(100, 300);

/**
 * Cache for thumbnail Object URLs
 * - Smaller entries (thumbnails are ~50KB)
 * - More entries, less memory per entry
 */
export const thumbnailCache = new LRUCache(500, 50);

/**
 * Cache for photo metadata (no URLs, just data)
 * - Very small entries
 * - Many entries
 */
export const metadataCache = new LRUCache(1000, 10);

// ============ Cache Utility Functions ============

/**
 * Get combined stats from all caches
 */
export function getAllCacheStats() {
  return {
    photos: photoUrlCache.stats,
    thumbnails: thumbnailCache.stats,
    metadata: metadataCache.stats
  };
}

/**
 * Clear all caches
 */
export function clearAllCaches() {
  photoUrlCache.clear();
  thumbnailCache.clear();
  metadataCache.clear();
  console.log('[PhotoCache] All caches cleared');
}

/**
 * Log cache stats (for debugging)
 */
export function logCacheStats() {
  const stats = getAllCacheStats();
  console.group('[PhotoCache] Cache Statistics');
  console.log('Photos:', stats.photos);
  console.log('Thumbnails:', stats.thumbnails);
  console.log('Metadata:', stats.metadata);
  console.groupEnd();
}

// ============ Helper for React DevTools ============

// Expose to window for debugging (development only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.__photoCache = {
    photos: photoUrlCache,
    thumbnails: thumbnailCache,
    metadata: metadataCache,
    stats: getAllCacheStats,
    clear: clearAllCaches
  };
}

export { LRUCache };
export default LRUCache;
